import { useEffect } from 'react';
import {
  activeThemeId,
  activeMode,
  resolveMode,
  useThemeStore,
  THEME_STORAGE_KEY,
} from '../theme/store';
import { getRegisteredTheme, themeRegistry } from '../theme/registry';

/**
 * Reflects the active theme + mode onto `<html>` as `data-theme` / `data-mode`. The actual token
 * values come from the generated `theme.css` (`[data-theme][data-mode]` selectors), so this only
 * flips attributes — no per-render style injection. It also keeps the document in sync when the OS
 * color-scheme changes while `mode === 'system'`.
 *
 * First paint is handled by {@link themeInitScript} (an inline `<head>` script) so there is no flash
 * of the wrong theme before React hydrates.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  // A surface-scoped override (e.g. admin theme, kid always-light mode) wins over the user's
  // persisted/persona choice. Selecting `mode` through `activeMode` means a scoped concrete mode also
  // short-circuits the `'system'` OS-preference listener below (it only runs while `mode === 'system'`).
  const themeId = useThemeStore((s) => activeThemeId(s));
  const mode = useThemeStore((s) => activeMode(s));

  useEffect(() => {
    const root = document.documentElement;
    // Guard against an unknown persisted id (e.g. a theme removed in a later release).
    const safeId = getRegisteredTheme(themeId) ? themeId : (themeRegistry[0]?.id ?? themeId);
    root.dataset.theme = safeId;
    root.dataset.mode = resolveMode(mode);
  }, [themeId, mode]);

  useEffect(() => {
    if (mode !== 'system' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      document.documentElement.dataset.mode = mql.matches ? 'dark' : 'light';
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [mode]);

  return children as React.ReactElement;
}

/**
 * Inline-script source for the document `<head>` (before the app bundle). Reads the persisted choice
 * synchronously and sets `data-theme` / `data-mode` so the very first paint is already themed.
 * Falls back to the first registered theme + system mode. Kept dependency-free and tiny.
 */
export function themeInitScript(): string {
  const fallbackId = themeRegistry[0]?.id ?? 'cozy-pastel';
  // Inline the known theme ids so the script can reject a stale persisted id (e.g. a theme removed/renamed in
  // a later release) and fall back — mirroring the provider's `getRegisteredTheme` guard. Without this the head
  // script would set `data-theme` to an unknown value with no matching CSS block and paint fully unstyled until
  // React mounts and corrects it.
  const knownIds = themeRegistry.map((t) => t.id);
  return `(function(){try{
var known=${JSON.stringify(knownIds)};
var fallback=${JSON.stringify(fallbackId)};
var raw=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});
var s=raw?(JSON.parse(raw).state||{}):{};
var id=s.themeId;
if(known.indexOf(id)===-1){id=fallback;}
var m=s.mode||'system';
if(m==='system'){m=window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}
var r=document.documentElement;r.dataset.theme=id;r.dataset.mode=m;
}catch(e){var r=document.documentElement;r.dataset.theme=${JSON.stringify(fallbackId)};r.dataset.mode='light';}})();`;
}
